import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Users, Clock, BadgeCheck, Shield, TrendingUp, ChevronRight } from 'lucide-react';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../context/LanguageContext';

const CampaignCard = ({ campaign, variant = 'default' }) => {
  const { t } = useLanguage();
  const progressPercent = Math.min((campaign.raised / campaign.goal) * 100, 100);
  
  // Fallback image for when image fails to load
  const fallbackImage = "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=500&fit=crop";
  
  const handleImageError = (e) => {
    e.target.src = fallbackImage;
    e.target.onerror = null; // Prevent infinite loop
  };

  // Get currency from campaign or default to USD
  const currency = campaign.currency || 'USD';
  
  const formatCurrency = (amount) => {
    // Use Intl.NumberFormat for proper currency formatting without duplication
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (variant === 'featured') {
    return (
      <Link to={`/campaign/${campaign.id}`} className="group block">
        <div className="bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-border">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={campaign.image || fallbackImage}
              alt={campaign.title}
              loading="lazy"
              decoding="async"
              onError={handleImageError}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-wrap gap-2">
              {campaign.trending && (
                <Badge className="bg-orange-500 text-white border-none flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> {t('trending')}
                </Badge>
              )}
              {campaign.verified && (
                <Badge className="bg-emerald-500 text-white border-none flex items-center gap-1">
                  <BadgeCheck className="w-3 h-3" /> {t('verified')}
                </Badge>
              )}
              {campaign.escrow && (
                <Badge className="bg-teal-500 text-white border-none flex items-center gap-1">
                  <Shield className="w-3 h-3" /> {t('escrow')}
                </Badge>
              )}
            </div>

            {/* Category Badge */}
            <div className="absolute top-3 right-3">
              <Badge variant="secondary" className="bg-card/90 text-foreground capitalize">
                {campaign.category}
              </Badge>
            </div>

            {/* Creator Info */}
            <div className="absolute bottom-3 left-3 flex items-center space-x-2">
              {campaign.creator.avatar ? (
                <img
                  src={campaign.creator.avatar}
                  alt={campaign.creator.name}
                  loading="lazy"
                  className="w-8 h-8 rounded-full border-2 border-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`w-8 h-8 rounded-full border-2 border-white bg-emerald-500 flex items-center justify-center text-white text-xs font-semibold ${campaign.creator.avatar ? 'hidden' : ''}`}>
                {campaign.creator.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="text-white text-sm">
                <span className="font-medium">{campaign.creator.name}</span>
                {campaign.creator.verified && (
                  <BadgeCheck className="inline w-3.5 h-3.5 ml-1 text-emerald-400" />
                )}
              </div>
            </div>
          </div>

          <div className="p-5">
            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
              {campaign.title}
            </h3>
            
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
              {campaign.story}
            </p>

            {/* Milestone Progress */}
            {campaign.milestone && (
              <div className="mb-4 p-3 bg-secondary rounded-lg">
                <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
                  <span>{t('milestone')} {campaign.milestone.current} / {campaign.milestone.total}</span>
                  <span className="font-medium text-emerald-400">{campaign.milestone.name}</span>
                </div>
                <div className="flex space-x-1">
                  {Array.from({ length: campaign.milestone.total }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded-full ${
                        i < campaign.milestone.current ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Progress Bar */}
            <div className="mb-3">
              <Progress value={progressPercent} className="h-2 bg-secondary" />
            </div>

            {/* Stats */}
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xl font-bold text-emerald-400">
                  {formatCurrency(campaign.raised)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('raised')} {t('of')} {formatCurrency(campaign.goal)} {t('goal')}
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center justify-end text-muted-foreground text-sm mb-1">
                  <Users className="w-4 h-4 mr-1" />
                  {campaign.donors.toLocaleString()} {t('donors')}
                </div>
                <div className="flex items-center justify-end text-muted-foreground text-sm">
                  <Clock className="w-4 h-4 mr-1" />
                  {campaign.daysLeft} {t('daysLeft')}
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center mt-3 pt-3 border-t border-border text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 mr-1 text-muted-foreground" />
              {campaign.location}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Compact variant
  return (
    <Link to={`/campaign/${campaign.id}`} className="group block">
      <div className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex border border-border">
        <div className="w-32 h-32 flex-shrink-0 overflow-hidden">
          <img
            src={campaign.image || fallbackImage}
            alt={campaign.title}
            loading="lazy"
            decoding="async"
            onError={handleImageError}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 p-4">
          <div className="flex items-center gap-2 mb-1">
            {campaign.verified && (
              <BadgeCheck className="w-4 h-4 text-emerald-400" />
            )}
            <span className="text-xs text-muted-foreground capitalize">{campaign.category}</span>
          </div>
          <h4 className="font-medium text-foreground group-hover:text-emerald-400 line-clamp-1 mb-2">
            {campaign.title}
          </h4>
          <Progress value={progressPercent} className="h-1.5 bg-secondary mb-2" />
          <div className="flex justify-between text-sm">
            <span className="font-semibold text-emerald-400">{formatCurrency(campaign.raised)}</span>
            <span className="text-muted-foreground">{Math.round(progressPercent)}%</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CampaignCard;
